// https://developers.atinternet-solutions.com/piano-analytics/data-collection/general/implementation-methods
// https://developers.atinternet-solutions.com/piano-analytics/data-collection/sdks/javascript
// https://developers.atinternet-solutions.com/piano-analytics/data-collection/how-to-send-events/privacy#default-privacy-modes
// 643882 – Mannheimer Morgen
// 643883 – Fränkische Nachrichten
// 643886 – Schwetzinger Zeitung
// 643885 – Bergstrasser Anzeiger
// 645826 – xmedias 5 Mannheimer Morgen

import { ConsoleDebug } from '../../../_utils/ConsoleDebug';
import { Environment } from "../../../_utils/Environment";
import { AccountData } from "../../account/service/AccountData";
import { PactoHelper } from "../../../_utils/PactoHelper";
import { PianoHelper } from "../../account/service/PianoHelper";

const toolToken = 'tool_1stparty_piano';

// Das Object ist wie folgt augebaut:
// Der Key stellt den Namen des Pacto Events dar
// Wichtig zu wissen ist, dass es zwei unterschiedliche Arten von Events gibt
// customEventPiano – Werden benötigt bei Components, welche in der AppJS stattfinden. Es wird ein CustomEvent gefeuert
// Alle anderen – Sind Components welche hier im AppHeaderJS liegen, kann mit pacto-events abgefruehstueckt werden
// Der Value beschreibt das jeweilige Piano Event
// Direct bedeutet, dass es direkt zu Piano geschickt werden soll und kein Eventlistener benötigt
const eventObj = {
  'topic-navigation': 'click.action',
  'top-navigation': 'click.action',
  'direct': 'page.display',
  'customEventPiano:link': 'click.action',
  'customEventPiano:link:download': 'content.download',
  'customEventPiano:sharing:share': 'content.share',
  'customEventPiano:sharing:copy': 'content.interaction',
  'customEventPiano:sharing:print': 'content.interaction',
  'customEventPiano:rightcol:button': 'click.action',
  'customEventPiano:teaser': 'click.teaser',
  'customEventPiano:formfield:button': 'click.action',
};

export class PianoAnalytics {
  constructor(context) {
    this.logInstance = new ConsoleDebug('debug-pa', 'PianoAnalytics');
    this.environment = new Environment();
    this.accountData = new AccountData(context);
    this.pactohelper = new PactoHelper();
    this.pianoHelper = new PianoHelper(context);
    this.context = context;
    this.metadata = {};
    this.pianoConfig = this.pianoHelper.getPianoConfigObj();

    this.handleEvents = this.handleEvents.bind(this);
    this.setUser = this.setUser.bind(this);
    this.createPageChapterObj = this.createPageChapterObj.bind(this);
    this.addPianoEventListener = this.addPianoEventListener.bind(this);
    this.sendPianoEvent = this.sendPianoEvent.bind(this);
    this.getArticleMetaInformation = this.getArticleMetaInformation.bind(this);
  }

  load() {
    const paUrl = 'https://tag.aticdn.net/piano-analytics.js';
    const hostname = this.environment.getDomain();

    this.loadDynamicLib(paUrl)
      .then(async () => {
        this.logInstance.log('Script wurde erfolgreich eingesetzt.');
        this.logInstance.log('this.pianoConfig', this.pianoConfig);
        this.logInstance.log('pianoAnalyticsSiteId: ', this.pianoConfig.analyticsConfigId);
        // edit AM products

        window.pa.setConfigurations({
          site: this.pianoConfig.analyticsConfigId,
          collectDomain: "https://kfqzkmc.pa-cd.com",
          addEventURL: true
        });
        this.logInstance.log('pa', window.pa);
        await this.setUser();

        if(this.environment.isArticleDetailPage()) {
          this.pactohelper.waitForObj('nfyArticleMetaInformations').then((event) => {
              this.handleEvents(window.nfyArticleMetaInformations);

          }).catch((error) => {
            this.logInstance.log('nfyArticleMetaInformations wurde nicht geladen.');
          });
        } else {
            this.handleEvents(window.nfyMetaInformations);
        }
      }).catch((error) => {
        this.logInstance.log('Error: ', error);
        this.logInstance.log('Script wurde nicht geladen.');
    });
  }

  loadDynamicLib(url, appendOnBody = false) {
    return new Promise((resolve, reject) => {
      let script = document.createElement('script');
      script.type = 'text/javascript';
      script.onload = resolve;
      script.onerror = reject;
      script.src = url;
      script.crossOrigin = "anonymous";

      this.logInstance.log('Script', script)
      if(!appendOnBody){
        document.head.append(script);
      }else{
        document.body.append(script);
      }
    });
  }

  handleEvents(metadata) {
    const initEvents = Object.entries(eventObj);

    let pageChapter =  this.createPageChapterObj();
    this.logInstance.log('metadata', metadata);

    initEvents.forEach(([eventName, eventAction]) => {
      switch (eventAction) {
        case 'page.display':

          pageChapter['content_format'] = window.nfyMetaInformations.module;
          if(this.environment.isArticleDetailPage()) {
            pageChapter['page'] = metadata.article_name;
            if(metadata.category.toUpperCase() === pageChapter['page_chapter1'].toUpperCase()) {
              pageChapter['page_chapter1'] = metadata.category;
            } else {
              pageChapter['page_chapter2'] = metadata.category;
            }
            this.sendPianoEvent(eventAction, this.getArticleMetaInformation(Object.assign(metadata,pageChapter)));
          } else {
            pageChapter.page = metadata.name;
            this.sendPianoEvent(eventAction, pageChapter);
          }
          break;
        case 'click.action':
        case 'click.teaser':
        case 'content.share':
        case 'content.interaction':
        case 'content.download':
          this.addPianoEventListener(eventName, eventAction, metadata);
          break;
      }
    });
  }

  // Am Ende des Reduktionsprozesses wird ein neues Objekt transformedObject erzeugt,
  // das entweder die gemappten Schlüssel aus keyMapping oder die Originalschlüssel
  // mit den entsprechenden Werten aus articleMeta enthält. Wenn kein Wert vorhanden ist,
  // wird ein leerer String eingesetzt.
  getArticleMetaInformation(articleMeta) {

    const keyMapping = {
      article_name: "content_article_name",
      size: "page_content_layout",
      update_date: "content_update_date",
      copyright: "content_copyright",
      region: "content_region",
      paid: "content_plus",
      source: "content_source",
      category: "content_category",
      length: "content_length"
    };

    const transformedObject = {};

    Object.entries(articleMeta).forEach(function([key, value]) {
      if(keyMapping[key]) {
        transformedObject[keyMapping[key]] = value;
      } else {
        transformedObject[key] = value;
      }
    });
    this.logInstance.log('transformedObject', transformedObject);

    return transformedObject;
  }

  createPageChapterObj() {

    // ['', 'politik_artikel,-politik-russischer-general-bei-au…on-nahe-moskau-getoetet-_arid,2271463_puid,3.html']
    // ['', 'startseite_puid,3.html']
    // ['', 'sport', 'vereine_verein,-adler-mannheim-_vereinid,1_puid,3.html']
    // ['', 'orte', 'mannheim', 'sandhofen.html']

    // ['/orte/mannheim/sandhofen.html']
    // ['/politik', 'artikel,-politik-russischer-general-bei-auto-explosion-nahe-moskau-getoetet-', 'arid,2271463', 'puid,3.html']
    // ['/orte/mannheim', 'puid,3.html']
    // ['/sport/vereine', 'verein,-rhein-neckar-loewen-', 'vereinid,2.html']
    // ['/']

    let pageChapterObj = {};
    let urlExplode =  window.location.pathname.split("_");
    let urlFolder = urlExplode[0].split("/");

    if(urlFolder.length > 2) {
      pageChapterObj['page_chapter1'] = urlFolder[1];
      pageChapterObj['page_chapter2'] = urlFolder[2];
    } else {
      pageChapterObj['page_chapter1'] = urlFolder[1];
    }
    return pageChapterObj;
  }

  async setUser() {
    let accountData = await this.accountData.getAccountData()
    this.logInstance.log('accountData', accountData);

    if(accountData.isLoggedIn) {
      this.logInstance.log('User ist eingeloggt. Dementsprechend wird auf jeden Fall pa.setUser() aufgerufen.');
      this.logInstance.log('setUser window.pa ', window.pa);
      this.logInstance.log('setUser accountData.hasAccessToDigitalPlus ', accountData.hasAccessToDigitalPlus);
      if(accountData.hasAccessToDigitalPlus) {
        this.logInstance.log('User hat ein Digital+ Abo.');
        this.logInstance.log('setUser Params accountData.userId ', accountData.userId.toString());
        this.logInstance.log('setUser window.pa hasAccessToDigitalPlus', window.pa);
        window.pa.setUser(accountData.userId.toString(), "premium", true);
      } else {
        this.logInstance.log('User hat kein Abo.');
        window.pa.setUser(accountData.userId.toString(), "freemium", true);
      }
    } else {
      window.pa.deleteUser();
    }
    localStorage.removeItem('setPianoOptIn');
  }

  getToolToken() {
    return toolToken;
  }

  addPianoEventListener(category, action, metadata) {

    if(category.includes("customEventPiano")) {

      let component = category.split(":")[1];
      let type = category.split(":")[2];

      this.logInstance.log('Custom Event gefunden: ', category);
      document.addEventListener(category, (event) => {
        this.logInstance.log('customEventPiano ', event);
        const { href, target, title, triggerComponent } = event.detail;

        let data = {
          "page": (metadata.name ? metadata.name : metadata.article_name),
          "page_chapter1": (metadata.page_chapter1 ? metadata.page_chapter1 : ""),
          "page_chapter2": (metadata.page_chapter2 ? metadata.page_chapter2 : ""),
          "content_format": window.nfyMetaInformations.module,
          "click": target.innerText
        };

        switch (component) {
          case 'link':
          case 'rightcol':
            data.link_url = href;
            break;
          case 'teaser':
            data.article_id = event.detail.article_id
            data.link_url = href;
            data.article_name = title;
            break;
        }

        switch (type) {
          case 'download':
            // WEB-538 special case for download links
            data.click = event.detail.download ?? '';
            break;
        }
        this.sendPianoEvent(action, data);
      })
    }

    this.context.on(`${category}:${action}`, (event) => {
      this.logInstance.log('event', event);
      const { target, parent } = event.data;
      let categoryValue = '';

      switch (category) {
        case 'topic-navigation':
          let categoryTopicList = parent?.parentNode?.parentNode;
          let categoryTopicListTitle = categoryTopicList.querySelector('.navigation__topic__title');
          categoryValue = categoryTopicListTitle?.title
          break;
        case 'top-navigation':
          categoryValue = target;
          break;
      }
      this.sendPianoEvent(action, {
        "page": (metadata.name ? metadata.name : metadata.article_name),
        [category]: categoryValue,
        "click": target
      });
    });
  }

  sendPianoEvent(action, obj) {
    let _paq = window._paq || [];
    _paq.push(['sendEvent', action, obj]);

    this.logInstance.log('sendEvent action', action);
    this.logInstance.log('sendEvent obj', obj);
    this.logInstance.log('_paq.push', ['sendEvent', action, obj]);
  }
}
